import React, { useMemo, useState, useEffect } from "react";
import { getCachedAccount } from "../../../utils/accountCache";
import { logger } from "../../shared/logger";

/**
 * Filter modal:
 * - Rooms/beds (beds, bedrooms, bathrooms)
 * - Amenities (comma-separated `amenities=id1,id2`)
 * - Tags (comma-separated `tags=id1,id2`)
 */

// Convert string to Title Case (Upper Camel Case for display)
// e.g., "swimming pool" -> "Swimming Pool", "air-conditioning" -> "Air-Conditioning"
function toTitleCase(str) {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(/[\s_-]+/) // Split by spaces, underscores, or hyphens
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function FilterModal({ palette, accountId, onApply, onClose }) {
  const getParam = (name) => new URLSearchParams(window.location.search).get(name);
  const pn = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
    };

  const [selectedAmenitiesData, setSelectedAmenitiesData] = useState([]);
  const [selectedTagsData, setSelectedTagsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [beds, setBeds] = useState(() => pn(getParam("beds")));
  const [bedrooms, setBedrooms] = useState(() => pn(getParam("bedrooms")));
  const [bathrooms, setBathrooms] = useState(() => pn(getParam("bathrooms")));

  // Initialize amenities from URL, converting to lowercase names only (filter out IDs)
  const [amenities, setAmenities] = useState(() => {
    const raw = getParam("amenities") || "";
    const list = raw.split(",").map((s) => s.trim()).filter(Boolean);
    // Filter out MongoDB ObjectIDs (24 hex characters) and only keep lowercase names
    const validNames = list.filter(item => !/^[0-9a-f]{24}$/i.test(item)).map(item => item.toLowerCase());
    logger.log("[FilterModal] Initial amenities from URL:", { raw, list, validNames });
    return new Set(validNames);
  });

  // Initialize tags from URL, converting to lowercase names only (filter out IDs)
  const [tags, setTags] = useState(() => {
    const raw = getParam("tags") || "";
    const list = raw.split(",").map((s) => s.trim()).filter(Boolean);
    // Filter out MongoDB ObjectIDs (24 hex characters) and only keep lowercase names
    const validNames = list.filter(item => !/^[0-9a-f]{24}$/i.test(item)).map(item => item.toLowerCase());
    logger.log("[FilterModal] Initial tags from URL:", { raw, list, validNames });
    return new Set(validNames);
  });

  // Fetch selected amenities and tags from account
  useEffect(() => {
    (async () => {
      try {
        if (!accountId) {
          setLoading(false);
          return;
        }

        const json = await getCachedAccount(accountId, "https://api.nexus.design");
        if (!json) {
          setLoading(false);
          return;
        }

        // Set selected amenities (sorted by order)
        // Handle new nested format: { amenity: {...}, order: 0 }
        if (json.selected_amenities && Array.isArray(json.selected_amenities)) {
          const flattenedAmenities = json.selected_amenities.map(item => {
            // If it's the new nested format with 'amenity' field
            if (item.amenity && typeof item.amenity === 'object') {
              return { ...item.amenity, order: item.order };
            }
            // Otherwise return as-is (old format or already flat)
            return item;
          });
          const sortedAmenities = [...flattenedAmenities].sort((a, b) => (a.order || 0) - (b.order || 0));
          setSelectedAmenitiesData(sortedAmenities);
        }

        // Set selected tags (sorted by order)
        // Handle new nested format: { tag: {...}, order: 0 }
        if (json.selected_tags && Array.isArray(json.selected_tags)) {
          const flattenedTags = json.selected_tags.map(item => {
            // If it's the new nested format with 'tag' field
            if (item.tag && typeof item.tag === 'object') {
              return { ...item.tag, order: item.order };
            }
            // Otherwise return as-is (old format or already flat)
            return item;
          });
          const sortedTags = [...flattenedTags].sort((a, b) => (a.order || 0) - (b.order || 0));
          setSelectedTagsData(sortedTags);
        }

        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch account data:", err);
        setLoading(false);
      }
    })();
  }, [accountId]);

  const toggleAmenity = (name) => {
    const nameStr = String(name).toLowerCase();
    setAmenities((prev) => {
      const next = new Set(prev);
      if (next.has(nameStr)) {
        next.delete(nameStr);
        logger.log(`[FilterModal] Removed amenity ${nameStr}, now:`, Array.from(next));
      } else {
        next.add(nameStr);
        logger.log(`[FilterModal] Added amenity ${nameStr}, now:`, Array.from(next));
      }
      return next;
    });
  };

  const toggleTag = (name) => {
    const nameStr = String(name).toLowerCase();
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(nameStr)) {
        next.delete(nameStr);
        logger.log(`[FilterModal] Removed tag ${nameStr}, now:`, Array.from(next));
      } else {
        next.add(nameStr);
        logger.log(`[FilterModal] Added tag ${nameStr}, now:`, Array.from(next));
      }
      return next;
    });
  };

  // ----- styles -----
  const overlay = {
    position: "fixed",
    inset: 0,
    background: "var(--swm-overlay, rgba(0,0,0,.35))",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };
  const panel = {
    background: "var(--swm-bg, #ffffff)",
    color: palette.fg,
    width: "var(--swm-w, 520px)",
    maxWidth: "95vw",
    maxHeight: "90vh",
    borderRadius: "var(--swm-r, 16px)",
    boxShadow: "var(--swm-shadow, 0 8px 24px rgba(2,6,23,.12))",
    border: "1px solid var(--swm-bc, rgba(15,23,42,.2))",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    overflow: "hidden",
  };
  const head = {
    padding: "var(--swm-pad, 16px) calc(var(--swm-pad, 16px) + 4px)",
    borderBottom: "1px solid var(--swm-div, rgba(148,163,184,.2))",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontFamily: "var(--swm-h-ff, inherit)",
    fontWeight: "var(--swm-h-fw, 800)",
    fontSize: "var(--swm-h-fs, 18px)",
    color: "var(--swm-h-fc, #0f172a)",
  };
  const body = { padding: "calc(var(--swm-pad, 16px) + 4px)", overflow: "auto" };
  const foot = {
    padding: "var(--swm-pad, 16px)",
    borderTop: "1px solid var(--swm-div, rgba(148,163,184,.2))",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  };

  const sectionTitle = {
    fontFamily: "var(--swm-title-ff, inherit)",
    fontWeight: "var(--swm-title-fw, 800)",
    fontSize: "var(--swm-title-fs, 20px)",
    color: "var(--swm-title-fc, #0f172a)",
    marginBottom: 12,
  };

  const ctrRow = {
    display: "grid",
    gridTemplateColumns: "1fr var(--sw-ctr-size, 32px) var(--sw-value-minw, 20px) var(--sw-ctr-size, 32px)",
    columnGap: "var(--sw-row-gap, 8px)",
    alignItems: "center",
    padding: "var(--sw-row-py, 10px) 0",
    borderBottom: "1px dashed var(--sw-row-bc, rgba(148,163,184,.2))",
  };
  const ctrRowLast = { ...ctrRow, borderBottom: "none" };

  const label = {
    fontFamily: "var(--sw-label-ff, inherit)",
    fontWeight: "var(--sw-label-fw, 700)",
    fontSize: "var(--sw-label-fs, 14px)",
    color: "var(--sw-label-fc, #0f172a)",
  };
  const valueText = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minWidth: "var(--sw-value-minw, 20px)",
    fontFamily: "var(--sw-value-ff, inherit)",
    fontWeight: "var(--sw-value-fw, 700)",
    fontSize: "var(--sw-value-fs, 14px)",
    color: "var(--sw-value-fc, #0f172a)",
  };

  // Center +/- buttons with flex
  const ctrBtn = (disabled) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "var(--sw-ctr-size, 32px)",
    height: "var(--sw-ctr-size, 32px)",
    borderRadius: "var(--sw-ctr-radius, 8px)",
    border: "1px solid var(--sw-ctr-bc, rgba(15,23,42,.2))",
    background: disabled
      ? "var(--sw-ctr-bg-disabled, rgba(127,127,127,.2))"
      : "var(--sw-ctr-bg, #ffffff)",
    color: "var(--sw-ctr-fc, #0f172a)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 700,
  });

  const clearLink = {
    background: "transparent",
    border: "none",
    color: "var(--swm-link-fc, #0f172a)",
    opacity: "var(--swm-link-op, .8)",
    cursor: "pointer",
    font: "inherit",
  };
  const cancelBtn = {
    background: "var(--swm-cancel-bg, transparent)",
    color: "var(--swm-cancel-fc, #0f172a)",
    border: "1px solid var(--swm-cancel-bc, rgba(15,23,42,.2))",
    borderRadius: "var(--swm-cancel-r, 10px)",
    padding: "var(--swm-cancel-py, 10px) var(--swm-cancel-px, 16px)",
    cursor: "pointer",
    fontFamily: "var(--swm-cancel-ff, inherit)",
    fontWeight: "var(--swm-cancel-fw, 700)",
    fontSize: "var(--swm-cancel-fs, 14px)",
  };
  const applyBtn = {
    background: "var(--swm-apply-bg, #f38b4a)",
    color: "var(--swm-apply-fc, #fff)",
    border: "1px solid var(--swm-apply-bc, transparent)",
    borderRadius: "var(--swm-apply-r, 10px)",
    padding: "var(--swm-apply-py, 10px) var(--swm-apply-px, 16px)",
    cursor: "pointer",
    fontFamily: "var(--swm-apply-ff, inherit)",
    fontWeight: "var(--swm-apply-fw, 700)",
    fontSize: "var(--swm-apply-fs, 14px)",
  };
  // Center the close X
  const closeX = {
    width: "var(--swm-close-size, 36px)",
    height: "var(--swm-close-size, 36px)",
    borderRadius: "var(--swm-close-r, 10px)",
    border: "1px solid var(--swm-close-bc, rgba(15,23,42,.2))",
    background: "var(--swm-close-bg, transparent)",
    color: "var(--swm-close-fc, #0f172a)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  };

  const amenityLabel = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    fontFamily: "var(--swm-check-ff, inherit)",
    fontWeight: "var(--swm-check-fw, 500)",
    fontSize: "var(--swm-check-fs, 14px)",
    color: "var(--swm-check-fc, #0f172a)",
  };

  const checkboxStyle = {
    width: "var(--swm-check-size, 18px)",
    height: "var(--swm-check-size, 18px)",
    cursor: "pointer",
    accentColor: "var(--swm-check-accent, #f38b4a)",
  };

  const onClearAll = () => {
    setBeds(0);
    setBedrooms(0);
    setBathrooms(0);
    setAmenities(new Set());
    setTags(new Set());
  };

  const onApplyClick = () => {
    const params = {
      beds: beds > 0 ? beds : "",
      bedrooms: bedrooms > 0 ? bedrooms : "",
      bathrooms: bathrooms > 0 ? bathrooms : "",
      amenities: amenities.size ? Array.from(amenities).join(",") : "",
      tags: tags.size ? Array.from(tags).join(",") : "",
    };
    onApply(params);
  };

  return (
    <div style={overlay} role="dialog" aria-label="Filters">
      <style>{`
        .nexus-filter-modal * { box-sizing: border-box; }
        .nexus-filter-modal input, .nexus-filter-modal button { font: inherit; }
        .nexus-filter-modal .nxsw-close-btn:hover {
          background: var(--swm-close-hover-bg, rgba(15,23,42,.05)) !important;
          color: var(--swm-close-hover-fc, #0f172a) !important;
          border-color: var(--swm-close-hover-bc, rgba(15,23,42,.3)) !important;
        }
        .nexus-filter-modal .nxsw-cancel-btn:hover {
          background: var(--swm-cancel-hover-bg, rgba(15,23,42,.05)) !important;
          color: var(--swm-cancel-hover-fc, #0f172a) !important;
          border-color: var(--swm-cancel-hover-bc, rgba(15,23,42,.3)) !important;
        }
        .nexus-filter-modal .nxsw-apply-btn:hover {
          background: var(--swm-apply-hover-bg, #ea580c) !important;
          color: var(--swm-apply-hover-fc, #ffffff) !important;
          border-color: var(--swm-apply-hover-bc, transparent) !important;
        }
        .nexus-filter-modal .nxsw-ctr-btn:hover:not(:disabled) {
          background: var(--swm-ctr-hover-bg, rgba(15,23,42,.05)) !important;
          border-color: var(--swm-ctr-hover-bc, rgba(15,23,42,.3)) !important;
          color: var(--swm-ctr-hover-fc, #0f172a) !important;
        }
        .nexus-filter-modal .nxsw-clear-link:hover {
          opacity: var(--swm-link-hover-op, 1) !important;
        }
      `}</style>

      <div className="nexus-filter-modal" style={panel}>
        <div style={head}>
          <div>Filters</div>
          <button type="button" className="nxsw-close-btn" onClick={onClose} aria-label="Close" style={closeX}>×</button>
        </div>

        <div style={body}>
          <div style={sectionTitle}>Rooms and beds</div>

          <div style={ctrRow}>
            <div style={label}><strong>Beds</strong></div>
            <button type="button" className="nxsw-ctr-btn" style={ctrBtn(beds <= 0)} disabled={beds <= 0} onClick={() => setBeds(Math.max(0, beds - 1))}>−</button>
            <div style={valueText} aria-live="polite">{beds}</div>
            <button type="button" className="nxsw-ctr-btn" style={ctrBtn(false)} onClick={() => setBeds(beds + 1)}>+</button>
          </div>

          <div style={ctrRow}>
            <div style={label}><strong>Bedrooms</strong></div>
            <button type="button" className="nxsw-ctr-btn" style={ctrBtn(bedrooms <= 0)} disabled={bedrooms <= 0} onClick={() => setBedrooms(Math.max(0, bedrooms - 1))}>−</button>
            <div style={valueText} aria-live="polite">{bedrooms}</div>
            <button type="button" className="nxsw-ctr-btn" style={ctrBtn(false)} onClick={() => setBedrooms(bedrooms + 1)}>+</button>
          </div>

          <div style={ctrRowLast}>
            <div style={label}><strong>Bathrooms</strong></div>
            <button type="button" className="nxsw-ctr-btn" style={ctrBtn(bathrooms <= 0)} disabled={bathrooms <= 0} onClick={() => setBathrooms(Math.max(0, bathrooms - 1))}>−</button>
            <div style={valueText} aria-live="polite">{bathrooms}</div>
            <button type="button" className="nxsw-ctr-btn" style={ctrBtn(false)} onClick={() => setBathrooms(bathrooms + 1)}>+</button>
          </div>

          {loading ? (
            <div style={{ ...sectionTitle, marginTop: 20, textAlign: "center", opacity: 0.6 }}>
              Loading filters...
            </div>
          ) : (
            <>
              {selectedAmenitiesData.length > 0 && (
                <>
                  <div style={{ ...sectionTitle, marginTop: 20 }}>Amenities</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 18px" }}>
                    {selectedAmenitiesData.map((amenity) => {
                      const nameKey = String(amenity.name).toLowerCase();
                      const isChecked = amenities.has(nameKey);
                      logger.log(`[FilterModal] Amenity ${amenity.name} (lowercase: ${nameKey}):`, {
                        isChecked,
                        amenitiesSet: Array.from(amenities),
                        hasCheck: amenities.has(nameKey)
                      });
                      return (
                        <label key={amenity._id} style={amenityLabel}>
                          <input
                            type="checkbox"
                            style={checkboxStyle}
                            checked={isChecked}
                            onChange={() => toggleAmenity(amenity.name)}
                          />
                          <span>{toTitleCase(amenity.name)}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              {selectedTagsData.length > 0 && (
                <>
                  <div style={{ ...sectionTitle, marginTop: 20 }}>Tags</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 18px" }}>
                    {selectedTagsData.map((tag) => {
                      const nameKey = String(tag.name).toLowerCase();
                      const isChecked = tags.has(nameKey);
                      logger.log(`[FilterModal] Tag ${tag.name} (lowercase: ${nameKey}):`, {
                        isChecked,
                        tagsSet: Array.from(tags),
                        hasCheck: tags.has(nameKey)
                      });
                      return (
                        <label key={tag._id} style={amenityLabel}>
                          <input
                            type="checkbox"
                            style={checkboxStyle}
                            checked={isChecked}
                            onChange={() => toggleTag(tag.name)}
                          />
                          <span>{toTitleCase(tag.name)}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div style={foot}>
          <button type="button" className="nxsw-clear-link" style={clearLink} onClick={onClearAll}>Clear all</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="nxsw-cancel-btn" style={cancelBtn} onClick={onClose}>Cancel</button>
            <button type="button" className="nxsw-apply-btn" style={applyBtn} onClick={onApplyClick}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}
